import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import styled from "styled-components";
import EntityForm from "../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../Common/utils";
import { withPropertyData } from "../../../../components/property-admin-data-provider";
import { PropertyConfigurationContext } from "../../../../components/property-configuration-provider";
import { ConditionTypeEnum } from "./domain/ConditionType.enum";
import { FlightHotelPaymentType } from "./domain/FlightHotelPaymentType.enum";
import { isAgrCondition } from "./domain/agr.helpers";
import { useChainData } from "../../../../components/chain-data-provider";
import { useGrpcClient, usePropertyGrpcCall } from "../../../../lib/grpc";
import { ConditionAPIPromiseClient } from "../../../../gen/witbooking/backoffice/condition/v1/condition_api_grpc_web_pb";
import { GetLinkedConditionsRequest } from "../../../../gen/witbooking/backoffice/condition/v1/condition_api_pb";
import { fetchAgrConditionData, saveAgrCondition } from "./infrastructure/conditionAgr.api";
import {
  normalizeConditionEntity,
  buildChainBody,
  buildAgrReadonlyLink,
  applyFlightHotelFixedValues,
  hasWithoutCardPaymentType,
  FLIGHT_HOTEL_FIXED_FIELDS,
} from "./application/conditionForm.helpers";

const AgrReadonlyBanner = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 13px;
`;

const AgrError = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  font-size: 13px;
`;

const messages = defineMessages({
  agrReadonlyMessage: {
    id: "app.conditionsForm.agrReadonlyMessage",
    defaultMessage:
      "This condition is managed from the aggregator engine. Only payment methods, cancellation policies and advanced translations can be edited independently on this motor.",
  },
  agrReadonlyEditRest: {
    id: "app.conditionsForm.agrReadonlyEditRest",
    defaultMessage: "To edit other fields,",
  },
  agrReadonlyLink: {
    id: "app.conditionsForm.agrReadonlyLink",
    defaultMessage: "click here",
  },
  agrMinOneMotorError: {
    id: "app.conditionsForm.agrMinOneMotorError",
    defaultMessage: "At least one child motor must be selected.",
  },
  agrSaveError: {
    id: "app.conditionsForm.agrSaveError",
    defaultMessage: "An error occurred while saving the condition. Please try again.",
  },
  agrUnlinkBlockedByInventory: {
    id: "app.conditionsForm.agrUnlinkBlockedByInventory",
    defaultMessage: "Cannot unlink motor {motor} because it has inventory lines created with this condition. ",
  },
  agrSaveErrorPolicyNotLinked: {
    id: "app.conditionsForm.agrSaveErrorPolicyNotLinked",
    defaultMessage: 'Error creating the condition. Motor "{motor}" does not have cancellation policy "{policy}" linked.',
  },
  motorSelectorAvailable: {
    id: "propertySelector.available",
    defaultMessage: "Available ({count})",
  },
  motorSelectorSelected: {
    id: "propertySelector.selected",
    defaultMessage: "Selected ({count})",
  },
  mutuallyExclusiveConditionTypes: {
    id: "app.conditionsForm.mutuallyExclusiveConditionTypes",
    description: "A condition cannot be both a store condition and a Flight + Hotel condition",
    defaultMessage:
      "A condition cannot be a store condition and a Flight + Hotel condition at the same time.",
  },
  flightHotelWithoutCardMissing: {
    id: "app.conditionsForm.flightHotelWithoutCardMissing",
    description: "A Flight + Hotel condition requires the engine to have the without_card payment method",
    defaultMessage:
      "A Flight + Hotel condition needs the \"No credit card needed\" (without_card) payment method, and this engine does not have it. Add it to the engine's payment methods before saving.",
  },
});

const EMPTY_ARRAY = [];

// Fixed superset of locales supported across all AGR child motors.
// Chosen instead of unioning each child's locales at runtime,
// which would require fetching every child motor's language config on form load.
const AGR_SUPPORTED_LOCALES = ["es", "ca", "eu", "da", "de", "nl", "en", "fr", "hu", "it", "ja", "ko", "pt", "ro", "ru", "zh"];

const AGR_READONLY_FIELDS = [
  "name", "htmlCancellation", "earlyCharge", "minimumCharge", "chargeFirstNight",
];

// Both virtual booleans are derived from the single stored enum, AFTER the base mapper runs
// (the base mapper collapses every boolean to "" via lodash isEmpty). At most one can be true.
// Module-scoped so it can be unit-tested via __test__ without the component's chain context.
function customEntityToFormValuesMapper({ entity, fields, locales, translationConfig }) {
  const formValues = entityToFormValuesMapper({ entity, fields, locales, translationConfig });
  formValues.conditionType = entity.conditionType === ConditionTypeEnum.STORE;
  formValues.isFlightHotelCondition = entity.conditionType === ConditionTypeEnum.FLIGHT_HOTEL;
  return formValues;
}

/** Maarlab: hides the charge fields while the V+H switch is on, keeping any visibility rule they already had. */
function hideWhenFlightHotel(field) {
  if (!FLIGHT_HOTEL_FIXED_FIELDS.includes(field.fieldName)) {
    return field;
  }
  const { visible } = field;
  return {
    ...field,
    visible: args =>
      !(args && args.formValues && args.formValues.isFlightHotelCondition) &&
      (typeof visible !== "function" || visible(args)),
  };
}

/** Resolves the persisted enum from the two virtual switches. Flight + Hotel wins. */
function resolveConditionType(formValues) {
  if (formValues.isFlightHotelCondition) {
    return ConditionTypeEnum.FLIGHT_HOTEL;
  }
  return formValues.conditionType ? ConditionTypeEnum.STORE : ConditionTypeEnum.ENGINE;
}

function ConditionsForm({
  config,
  locales,
  entity,
  entityError,
  entityResult,
  entityName,
  loading,
  getListPath,
  relatedEntities,
  onSubmitEntity,
  hotelTicker,
}) {
  const history = useHistory();
  const intl = useIntl();
  const chainData = useChainData();
  const isChain = !!chainData?.isChain;
  const chainChildren = chainData?.children || EMPTY_ARRAY;
  const propertyConfig = useContext(PropertyConfigurationContext);
  const onlineStoreActive = propertyConfig && propertyConfig.onlineStoreActive === "1";
  const maarlabActive = Boolean(propertyConfig && propertyConfig.isMaarlabActive === "1");

  const conditionsClient = useGrpcClient(ConditionAPIPromiseClient);
  const getLinkedConditions = usePropertyGrpcCall(conditionsClient, conditionsClient.getLinkedConditions);

  const [agrSaving, setAgrSaving] = useState(false);
  const [agrSaveError, setAgrSaveError] = useState(null);
  const agrErrorRef = useRef(null);

  // Map of propertyTicker → inUse for child conditions (chain edit mode)
  const [linkedConditionsInUse, setLinkedConditionsInUse] = useState({});

  useEffect(() => {
    if (agrSaveError && agrErrorRef.current) {
      agrErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [agrSaveError]);

  // Fetch linked conditions in_use status for chain edit mode
  useEffect(() => {
    if (!isChain || !entity?.ticker) return;
    const request = new GetLinkedConditionsRequest();
    request.setConditionTicker(entity.ticker);
    getLinkedConditions(request)
      .then(response => {
        const inUseMap = {};
        response.getConditionsList().forEach(c => {
          inUseMap[c.getPropertyTicker()] = c.getInUse();
        });
        setLinkedConditionsInUse(inUseMap);
      })
      .catch(err => {
        console.warn("Could not fetch linked conditions in_use status", err);
        setLinkedConditionsInUse({});
      });
  }, [isChain, entity?.ticker, getLinkedConditions]);

  // Fetch AGR data: childPropertyTickers (chain edit) or parentPropertyTicker (individual readonly)
  const [agrChildTickers, setAgrChildTickers] = useState(undefined);
  const [resolvedParentPropertyTicker, setResolvedParentPropertyTicker] = useState(null);

  useEffect(() => {
    const propertyTicker = entity?.propertyTicker || entity?.hotelTicker || hotelTicker;
    if (!entity?.ticker || !propertyTicker) return;

    if (isChain) {
      const controller = new AbortController();
      const baseURL = config.restConfig?.baseURL || "";
      fetchAgrConditionData(baseURL, propertyTicker, entity.ticker, controller.signal)
        .then(data => {
          const parentEntry = data[propertyTicker];
          setAgrChildTickers(
            Array.isArray(parentEntry?.childPropertyTickers) ? parentEntry.childPropertyTickers : []
          );
        })
        .catch(err => {
          if (err.name !== "CanceledError" && err.name !== "AbortError") setAgrChildTickers([]);
        });
      return () => controller.abort();
    }

    if (isAgrCondition(entity)) {
      if (entity.parentPropertyTicker) {
        setResolvedParentPropertyTicker(entity.parentPropertyTicker);
        return;
      }
      const controller = new AbortController();
      const baseURL = config.restConfig?.baseURL || "";
      fetchAgrConditionData(baseURL, propertyTicker, entity.ticker, controller.signal)
        .then(data => {
          const parentKey = Object.keys(data).find(key => key !== propertyTicker);
          if (parentKey) setResolvedParentPropertyTicker(parentKey);
        })
        .catch(err => {
          if (err.name !== "CanceledError" && err.name !== "AbortError") {
            console.warn("Could not resolve AGR parent property ticker", err);
          }
        });
      return () => controller.abort();
    }

    setAgrChildTickers(undefined);
    setResolvedParentPropertyTicker(null);
  }, [isChain, entity?.ticker, entity?.propertyTicker, entity?.hotelTicker,
      entity?.isLinkedToAggregator, entity?.parentPropertyTicker, hotelTicker, config.restConfig?.baseURL]);

  const entityWithChildren = useMemo(() => {
    if (!isChain || agrChildTickers === undefined) return entity;
    return { ...entity, childPropertyTickers: agrChildTickers };
  }, [entity, isChain, agrChildTickers]);

  const isAgrLinkedInIndividual = useMemo(
    () => !isChain && isAgrCondition(entity || {}),
    [isChain, entity]
  );

  const agrReadonlyLink = useMemo(() => {
    const parentPropertyTicker = entity?.parentPropertyTicker || resolvedParentPropertyTicker;
    if (!isAgrLinkedInIndividual || !parentPropertyTicker) return null;
    const conditionTicker = entity?.parentTicker || entity?.ticker;
    return buildAgrReadonlyLink(parentPropertyTicker, conditionTicker, entityName);
  }, [isAgrLinkedInIndividual, entity, resolvedParentPropertyTicker, entityName]);

  const { name, validation, renderers, translation, locale, entities } = config;

  // relatedEntities.cancellationPolicy comes from the individual GET endpoint which
  // already returns only the policies applicable to this motor (own + AGR-linked).
  // In AGR mode, policies have no _agr suffix (that's only on child motors),
  // so no filtering is needed — all policies returned by the backend are valid options.

  const computedFields = useMemo(() => {
    const formFields = (config.fields && config.fields.form) || [];
    const overriddenFields = formFields.map(field => {
      if (field.fieldName === "conditionType") {
        return { ...field, visible: () => onlineStoreActive };
      }
      // Maarlab: the Flight + Hotel switch, gated on the property's Maarlab flag.
      if (field.fieldName === "isFlightHotelCondition") {
        return { ...field, visible: () => maarlabActive };
      }
      // Maarlab: the prepayment selector, shown only when Maarlab is on AND the V+H switch is on.
      if (field.fieldName === "flightHotelPaymentType") {
        return {
          ...field,
          visible: ({ formValues }) =>
            maarlabActive && Boolean(formValues && formValues.isFlightHotelCondition),
        };
      }
      if (field.fieldName === "color") {
        return { ...field, visible: () => false };
      }
      if (field.fieldName === "payments" && isChain) {
        return { ...field, visible: () => false };
      }
      if (isAgrLinkedInIndividual && AGR_READONLY_FIELDS.includes(field.fieldName)) {
        return { ...field, disabled: true };
      }
      return field;
    });
    return overriddenFields.map(hideWhenFlightHotel);
  }, [config.fields, isChain, onlineStoreActive, maarlabActive, isAgrLinkedInIndividual]);

  const mapEntityToFormValues = useCallback(
    ({ entity: ent, fields, locales: locs, translationConfig }) => {
      const formValues = customEntityToFormValuesMapper({
        entity: ent, fields, locales: locs, translationConfig,
      });
      formValues._isChain = isChain;
      formValues._chainChildren = chainChildren;
      formValues._availableLabel = (count) => intl.formatMessage(messages.motorSelectorAvailable, { count });
      formValues._selectedLabel = (count) => intl.formatMessage(messages.motorSelectorSelected, { count });
      if (isChain && (!formValues.payments || formValues.payments.length === 0)) {
        formValues.payments = ["reservation_request"];
      }
      const rawSelectedMotors = Array.isArray(ent.childPropertyTickers)
        ? ent.childPropertyTickers
        : Array.isArray(ent.motorsList) ? ent.motorsList : [];
      // Only keep motors that are still actual children of the AGR chain.
      // childPropertyTickers may include stale motors removed from the chain but
      // still linked in the condition — react-dual-listbox v2 preserves "hidden
      // selected" items and re-emits them in onChange, corrupting the PUT body.
      formValues.selectedMotors = isChain
        ? rawSelectedMotors.filter(t => chainChildren.includes(t))
        : rawSelectedMotors;
      return formValues;
    },
    [isChain, chainChildren, intl]
  );

  /**
   * Maarlab reactive mutual exclusion: turning either condition-kind switch ON clears the other
   * in the same setState. Turning one OFF touches nothing, and every other field passes through.
   */
  const beforeFormStateSetHook = useCallback(({ field, newState }) => {
    if (field !== "conditionType" && field !== "isFlightHotelCondition") {
      return newState;
    }
    const other = field === "conditionType" ? "isFlightHotelCondition" : "conditionType";
    if (!newState.formValues[field]) {
      return newState;
    }
    return {
      ...newState,
      formValues: { ...newState.formValues, [other]: false },
    };
  }, []);

  /**
   * Maarlab: the mutual-exclusion check is a defensive net for corrupt server data only —
   * beforeFormStateSetHook makes that state unreachable through the UI. The without_card check
   * blocks saving a V+H condition on an engine that cannot take Maarlab's reservations.
   */
  const customValidate = useCallback(
    formValues => {
      if (formValues.conditionType && formValues.isFlightHotelCondition) {
        return intl.formatMessage(messages.mutuallyExclusiveConditionTypes);
      }
      if (
        formValues.isFlightHotelCondition &&
        !hasWithoutCardPaymentType(relatedEntities && relatedEntities.paymentTypes)
      ) {
        return intl.formatMessage(messages.flightHotelWithoutCardMissing);
      }
      return "";
    },
    [intl, relatedEntities]
  );

  const handleSubmit = useCallback(
    ({ formValues }) => {
      let conditionEntity = normalizeConditionEntity(formValues, config.translation);

      // Maarlab: derive the persisted enum from the two virtual switches (Flight + Hotel wins),
      // overriding the STORE/ENGINE value normalizeConditionEntity computed from conditionType.
      conditionEntity.conditionType = resolveConditionType(formValues);

      // Maarlab: the charge fields are hidden for V+H, so whatever they held is replaced here.
      if (conditionEntity.conditionType === ConditionTypeEnum.FLIGHT_HOTEL) {
        conditionEntity = applyFlightHotelFixedValues(conditionEntity);
      }

      // Explicit null, never omission: conditionEntity is a copy of formValues, so the selector's
      // FLIGHT_ONLY default would otherwise ride along on every ENGINE/STORE condition. Sending
      // null also clears the stored value when a PUT demotes a V+H condition back to ENGINE/STORE.
      conditionEntity.flightHotelPaymentType =
        conditionEntity.conditionType === ConditionTypeEnum.FLIGHT_HOTEL
          ? formValues.flightHotelPaymentType || FlightHotelPaymentType.FLIGHT_ONLY
          : null;

      // Virtual field, never persisted.
      delete conditionEntity.isFlightHotelCondition;

      if (isChain) {
        const selectedMotors = formValues.selectedMotors || [];
        if (selectedMotors.length === 0) {
          setAgrSaveError(intl.formatMessage(messages.agrMinOneMotorError));
          return;
        }

        // Validate: motors being unlinked must not have inventory lines (in_use)
        const previousMotors = agrChildTickers || [];
        const removedMotors = previousMotors.filter(m => !selectedMotors.includes(m));
        const blockedMotor = removedMotors.find(m => linkedConditionsInUse[m]);
        if (blockedMotor) {
          setAgrSaveError(intl.formatMessage(messages.agrUnlinkBlockedByInventory, { motor: blockedMotor }));
          return;
        }

        const propertyTicker = conditionEntity.propertyTicker || hotelTicker;
        const chainBody = buildChainBody(conditionEntity, propertyTicker, selectedMotors);
        const baseURL = config.restConfig?.baseURL || "";

        setAgrSaving(true);
        saveAgrCondition(baseURL, propertyTicker, conditionEntity.ticker, chainBody, !!formValues.id)
          .then(() => {
            setAgrSaveError(null);
            history.push(getListPath({ hotelTicker: propertyTicker, entityName }));
          })
          .catch(error => {
            console.error("Error saving AGR condition", error);
            const data = error.response?.data;
            const rawMessage = typeof data === "string"
              ? data
              : (typeof data?.message === "string" ? data.message : "");
            if (rawMessage.toLowerCase().includes("cancellation")) {
              const motorMatch = rawMessage.match(/found for (.+?) property/);
              const motor = motorMatch?.[1] || "";
              const policy = (conditionEntity.policies || []).join(", ");
              setAgrSaveError(
                motor && policy
                  ? intl.formatMessage(messages.agrSaveErrorPolicyNotLinked, { motor, policy })
                  : intl.formatMessage(messages.agrSaveError)
              );
            } else {
              setAgrSaveError(intl.formatMessage(messages.agrSaveError));
            }
          })
          .finally(() => setAgrSaving(false));
      } else {
        onSubmitEntity({ entity: conditionEntity });
      }
    },
    [config, isChain, onSubmitEntity, intl, history, getListPath, entityName, hotelTicker, agrChildTickers, linkedConditionsInUse]
  );

  return (
    <div>
      {isAgrLinkedInIndividual && (
        <AgrReadonlyBanner>
          {intl.formatMessage(messages.agrReadonlyMessage)}
          {" "}
          {intl.formatMessage(messages.agrReadonlyEditRest)}{" "}
          {agrReadonlyLink ? (
            <a href={agrReadonlyLink}>
              {intl.formatMessage(messages.agrReadonlyLink)}
            </a>
          ) : (
            intl.formatMessage(messages.agrReadonlyLink)
          )}
        </AgrReadonlyBanner>
      )}
      <EntityForm
        loading={loading || agrSaving}
        name={name}
        entity={entityWithChildren}
        entities={entities}
        validation={validation}
        renderers={renderers}
        fields={computedFields}
        locales={isChain ? AGR_SUPPORTED_LOCALES : locales}
        translation={translation}
        onSubmit={handleSubmit}
        beforeFormStateSetHook={beforeFormStateSetHook}
        customValidate={customValidate}
        entityError={entityError}
        entityResult={entityResult}
        entityName={entityName}
        getListPath={getListPath}
        entityToFormValuesMapper={mapEntityToFormValues}
        relatedEntities={relatedEntities}
        locale={locale}
      />
      {agrSaveError && (
        <AgrError ref={agrErrorRef}>{agrSaveError}</AgrError>
      )}
    </div>
  );
}

ConditionsForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
  entity: {},
  getListPath: function getListPath({ hotelTicker, entityName }) {
    return `/${hotelTicker}/${entityName}/`;
  },
};

export const __test__ = { customEntityToFormValuesMapper, resolveConditionType, hideWhenFlightHotel };

export default withPropertyData(ConditionsForm);
